/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Subject, EMPTY, of, Observable } from 'rxjs';
import {
	filter,
	tap,
	takeUntil,
	bufferTime,
	map,
	delay,
	pluck,
	distinctUntilChanged,
	switchMap,
	take,
	buffer,
} from 'rxjs/operators';
import {
	alternativeBufferTime,
	combineSequence,
} from 'src/app/plain-javascript/services/rxjs-internal-extensions';
import deepcopy from 'deepcopy';
import { Rectangle } from 'pixi.js-legacy';

import { calcBoundingRectFromTickets } from 'utils/ticket-utils';

import { TicketList } from 'src/app/common/fancy-firebase-ticket/lists/TicketList';
import {
	TicketActionTypes,
	FancyTicketAction,
	TicketChangedToDragAction,
} from 'src/app/app-data/actions/ticket-actions';
import { PlanState } from 'src/app/services/plan-state.service';

import { UndoItem } from './UndoItem';
import { UndoTypes, UndoTitles } from './UndoTypes';
import { BaseUndoOperation } from './base-undo-operation';
import { activeLineInvalidationCheck, monitorExternalDeletion } from './undo-utils';
import { PlanObject } from '../fancy-firebase/objects/PlanObject';

import { Ticket } from '@touchplan/common';

// ### Player 2: The Operation
// - at any given point there will be one instance of each supported operation type
// - these are expected to be created when state is defined and registered with the undo service (then cleaned up when state is lost)
// - each operation implements an Operation interface which the undo service will rely on, each type having it's own very specific
// state data and mappings
// - responsible for monitoring application state, both to push undoable operations into the tree and to maintain invalidation
// - responsible for converting relevant application state to and from a cachable format. Actual storing and retrieval will be
// managed by the undo service.
// - transformed operation data or anything related to undo should not be stored on this service. Application state data _may_
// have some reason to be stored here, but nothing comes to mind.
// - ideally types/ interfaces will be defined per type which define the shape the cache data will take

//steps
// 1) just create the move operation
// 2) create interface(s) based on it, likely a primary one and one for things that listen directly to a fancy firebase list. That one
// may want to be an abstract class instead

// - the method of registering this with the undo service will be roughly inspired by angular's [[ControlValueAccessor]]
// https://angular.io/api/forms/ControlValueAccessor
// - will try and note all interface methods with a "interface" comment

interface MoveInvalidationCache {
	boundingRect: Rectangle;
	ticketIds: { [key: string]: string };
	activeLineX: number;
	multipleActiveLines?: boolean;
}

/** type the data and invalidation cache fields of the default UndoItem */
interface MoveUndoItem extends UndoItem {
	data: [Ticket[], Ticket[]];
	invalidationCache: MoveInvalidationCache;
}

// export class TicketMoveOperation implements UndoOperationInterface<MoveInvalidationCache> {
export class TicketMoveOperation extends BaseUndoOperation<MoveInvalidationCache, MoveUndoItem> {
	public readonly undoType = UndoTypes.ticketMove; //maybe make instance? Or maybe even remove?

	//interface
	public title = UndoTitles.ticketMove;

	//pass in state info
	constructor(
		private ticketList: TicketList,
		private planObject: PlanObject,
		private planState: PlanState,
		private previousDeletion$: Observable<FancyTicketAction[]>
	) {
		super();
		//collect the drag start list
		const dragStart$ = ticketList.rawEvent$.pipe(
			filter(
				(action) =>
					action.type === TicketActionTypes.ticketChangedToDrag &&
					(action as TicketChangedToDragAction).config &&
					(action as TicketChangedToDragAction).config.isUndoable
			),
			//filter out tickets from the floating list
			filter((action) => !action.payload.isAFloatingTicket),
			//cute, the data has updated by the time this is deepcopied if this is done after the bufferTime. So moving here.
			// could also grab data from the database section too in theory
			map((action) => deepcopy(action)),
			alternativeBufferTime(20),
			map((tickets) => tickets.map((t) => t.payload))
		);

		//collect the drag end list
		const dragEnd$ = ticketList.rawEvent$.pipe(
			// tap(a => logger.log('all emissions', a)),
			filter((action) => action.type === TicketActionTypes.ticketChangedToDefault),
			//filter out tickets from the floating list
			filter((action) => !action.payload.isAFloatingTicket),
			// tap(a => { logger.log('changed to default', a); }),
			//cute, the data has updated by the time this is deepcopied if this is done after the bufferTime. So moving here.
			// could also grab data from the database section too in theory
			map((action) => deepcopy(action)),
			alternativeBufferTime(20),
			map((tickets) => tickets.map((t) => t.payload))
		);

		combineSequence([dragStart$, dragEnd$])
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((moveTuple) => {
				this.onPush(this.makeUndoItem(moveTuple as [Ticket[], Ticket[]]));
			});

		//monitor ticket removal for invalidation
		//TODO - enhancement, after filtering, group by ticket id, audit for a bit to try and only emit once per ticket id
		// so as to better handle that fact that there are multiple deletion events, usually called in sequence
		ticketList.rawEvent$
			.pipe(monitorExternalDeletion(this.previousDeletion$), takeUntil(this.shutdownSubject$))
			.subscribe((action) => {
				this.checkDeleteInvalidation(action.key);
			});

		//monitor active line position for invalidation
		//TODO - switch data source to active line observable once it exists
		this.planObject.object$
			.pipe(
				filter((a) => !!a),
				pluck('view'),
				pluck('activeLineX'),
				distinctUntilChanged(),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe((activeLineX) => {
				this.checkActiveLineInvalidation(activeLineX);
			});
	}

	private makeUndoItem(data: [Ticket[], Ticket[]]): MoveUndoItem {
		return {
			title: this.title,
			data,
			undoType: this.undoType,
			invalidationCache: this.makeInvalidationCache(data),
		};
	}

	private makeInvalidationCache(data: [Ticket[], Ticket[]]): MoveInvalidationCache {
		const ids = {};
		data.forEach((list) => {
			list.forEach((a) => (ids[a.$id] = a.$id));
		});
		return {
			boundingRect: calcBoundingRectFromTickets([...data[0], ...data[1]]), //bad perf
			ticketIds: ids,
			activeLineX: this.planState.plan._internalObject.view.activeLineX,
		};
	}

	//maybe this wants to have an array of id's passed in?
	private checkDeleteInvalidation(ticketId: string) {
		this.onVerifyInvalidation((cache: MoveInvalidationCache, list: MoveUndoItem[]) => {
			if (!cache.ticketIds[ticketId]) {
				return null;
			}
			return list.filter((item) => item.invalidationCache.ticketIds[ticketId]); //TODO - it's apparently possible to not have ticketIds?
		});
	}

	private checkActiveLineInvalidation(activeLineX: number) {
		this.onVerifyInvalidation((cache: MoveInvalidationCache, list: MoveUndoItem[]) => {
			if (!this.activeLineChecks(activeLineX, cache)) {
				return null;
			}
			return list.filter((item) =>
				this.activeLineChecks(activeLineX, item.invalidationCache as MoveInvalidationCache)
			);
		});
	}

	//return true if invalid
	private activeLineChecks(activeLineX: number, cache: MoveInvalidationCache) {
		if (cache.multipleActiveLines) {
			return true;
		}
		return activeLineInvalidationCheck(activeLineX, cache.activeLineX, cache.boundingRect);
	}

	private genericUndo(cacheData: Ticket[]) {
		if (cacheData && cacheData.length) {
			cacheData.forEach((ticket) => {
				this.planState.actions.updateTicketLiveData(ticket.$id, ticket.view, undefined, { overwrite: true });
			});
			this.planState.actions.persistTicketData(cacheData);
		}
		return EMPTY; //placeholder, since updateTicketLiveData runs more or less immediately
	}

	public applyUndo(undoItem: MoveUndoItem) {
		return this.genericUndo(undoItem.data[0]);
	}

	public applyRedo(undoItem: MoveUndoItem) {
		return this.genericUndo(undoItem.data[1]);
	}

	// just always recalc for now...
	public calculateCombinedInvalidation(list: MoveUndoItem[], existingCache: MoveInvalidationCache) {
		let boundingRect: Rectangle;
		const ticketIds = {};
		let activeLineX: number;
		let multipleActiveLines = false;

		list.forEach((item) => {
			// tslint:disable-next-line: forin
			for (const t in item.invalidationCache.ticketIds) {
				ticketIds[t] = t;
			}
			if (!boundingRect) {
				boundingRect = item.invalidationCache.boundingRect;
			} else {
				boundingRect.enlarge(item.invalidationCache.boundingRect);
			}

			if (activeLineX === undefined) {
				activeLineX = item.invalidationCache.activeLineX;
			} else if (activeLineX !== item.invalidationCache.activeLineX) {
				multipleActiveLines = true;
			}
		});

		return { boundingRect, ticketIds, activeLineX, multipleActiveLines };
	}
}
